/**
 * Amortization Schedule Table Component
 * Displays the amortization schedule for a contract
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon, Info as InfoIcon, PictureAsPdf as PdfIcon, Edit as EditIcon, Functions as FunctionsIcon, KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon, UnfoldMore as ExpandAllIcon, UnfoldLess as CollapseAllIcon, Flag as FlagIcon, AccountBalanceWallet as WalletIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { exportAmortizationSchedule } from '../../utils/excelExport';
import { formatNumber, formatRateType, formatShortDate } from '../../utils/formatters';
import CalculationDetailPopover from './CalculationDetailPopover';
import { useTenant } from '../../contexts/TenantContext';
import { getNarrationChipProps, getNarrationFlowType as getFlowType, getNarrationFlowLabel, isAdjustmentNarration, formatNarrationAmount, getNarrationRowSx, NARRATION_ROW_STYLES, ADJUSTMENT_CHIP_COLOR } from '../../utils/narration';
import { groupAmortizationSchedule } from '../../utils/groupAmortizationSchedule';
import { isPeriodAccrual } from '../../utils/scheduleComparison';
import usePagination from '../../hooks/usePagination';

// The schedule-version chip. A TERMINATION version is the last one a contract ever gets — the schedule
// was closed out early — so it reads "VT" rather than "V3", which would say nothing about why the
// schedule stops there. The underlying version_number stays sequential (it is the ordering key the
// active-version lookup and the next-version derivation both depend on); only the label changes.
function versionChipProps(versionNumber, versionType) {
  if (!versionNumber) return null;
  const terminal = versionType === 'termination';
  return {
    label: terminal ? 'VT' : `V${versionNumber}`,
    color: terminal ? 'warning' : 'info',
    title: terminal
      ? `Version ${versionNumber} — contract terminated; no further schedule follows`
      : `Schedule version ${versionNumber}`,
  };
}

function AmortizationScheduleTable({ schedule: scheduleProp, variances, contractId, onRegenerate, canAdmin, contract = {}, currencyCode, onDownloadPDF, isGeneratingPDF, onOverrideRequest, onShowOverrides, readOnly = false, hideRegenerateButton = false, previewMode = false, showAllRows = false }) {
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;
  // Paginated contexts (contract detail, approval) use the shared pagination hook
  // with the standard rowsPerPageOptions ([5,10,25,50]). Wizard / preview contexts
  // pass showAllRows to render the entire schedule with no pager (see usage below).
  const pagination = usePagination({ defaultRowsPerPage: 25 });
  const [auditAnchorEl, setAuditAnchorEl] = useState(null);
  const [auditEntryId, setAuditEntryId] = useState(null);
  // Preview projections aren't persisted (no id); they carry an inline calc_detail
  // record the popover renders directly instead of fetching audit rows by id.
  const [auditInlineRecord, setAuditInlineRecord] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [paramsExpanded, setParamsExpanded] = useState(false);
  const [cashFlowExpanded, setCashFlowExpanded] = useState(false);

  const handleAuditClick = (event, entry) => {
    if (entry.id) {
      setAuditAnchorEl(event.currentTarget);
      setAuditEntryId(entry.id);
      setAuditInlineRecord(null);
    } else if (entry.calc_detail) {
      // Preview projection: render the inline record, no fetch.
      setAuditAnchorEl(event.currentTarget);
      setAuditEntryId(null);
      setAuditInlineRecord(entry.calc_detail);
    }
  };

  // Render the per-row variance chip from the contract_payment_variance map.
  // Returns null when there's no variance row or the variance is < 0.01.
  // Sits beside the Status chip; does NOT replace it (regression-safe).
  const renderVarianceChip = (scheduleId) => {
    if (!variances || !scheduleId) return null;
    const v = variances.get(scheduleId);
    if (!v) return null;
    const amount = parseFloat(v.variance_amount);
    if (!Number.isFinite(amount) || Math.abs(amount) < 0.01) return null;
    const ccy = currencyCode || contract?.currency_code || '';
    const isShort = amount < 0;
    const tooltip = (
      <Box sx={{ p: 0.5 }}>
        {v.variance_reason && (
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
            {v.variance_reason}
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: 'block' }}>
          Allocated interest: {ccy} {formatNumber(v.allocated_interest)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Allocated capital: {ccy} {formatNumber(v.allocated_capital)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Cash received: {ccy} {formatNumber(v.cash_received)}
        </Typography>
      </Box>
    );
    return (
      <Tooltip title={tooltip} arrow>
        <Chip
          label={`${isShort ? 'Short' : 'Over'} ${ccy} ${formatNumber(Math.abs(amount))}`}
          size="small"
          color={isShort ? 'error' : 'success'}
          variant="outlined"
          sx={{ ml: 0.5 }}
        />
      </Tooltip>
    );
  };

  // T106/T108: render a Calculated-vs-Adjusted variance chip on an edited row.
  // The engine value is preserved on the row (original_interest_amount /
  // original_capital_amount); the live (manual) value is interest_amount /
  // capital_amount. Used for both apportioned-interest edits (on the payment +
  // its accruals) and free-capital edits (T108). `field` is 'interest' | 'capital'.
  const renderVarianceChipFor = (entry, field) => {
    if (!entry?.has_override) return null;
    const calc = parseFloat(field === 'capital' ? entry.original_capital_amount : entry.original_interest_amount);
    const adj = parseFloat(field === 'capital' ? entry.capital_amount : entry.interest_amount);
    if (!Number.isFinite(calc) || !Number.isFinite(adj)) return null;
    const delta = adj - calc;
    if (Math.abs(delta) < 0.01) return null;
    const ccy = currencyCode || contract?.currency_code || '';
    const tooltip = (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Calculated (engine): {ccy} {formatNumber(calc)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Adjusted (manual): {ccy} {formatNumber(adj)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Variance: {delta > 0 ? '+' : ''}{ccy} {formatNumber(delta)}
        </Typography>
      </Box>
    );
    return (
      <Tooltip title={tooltip} arrow>
        <Chip
          label={`Adj ${delta > 0 ? '+' : ''}${formatNumber(delta)}`}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ ml: 0.5 }}
        />
      </Tooltip>
    );
  };

  const handleAuditClose = () => {
    setAuditAnchorEl(null);
    setAuditEntryId(null);
    setAuditInlineRecord(null);
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Ensure schedule is always a referentially stable array
  const schedule = useMemo(() => Array.isArray(scheduleProp) ? scheduleProp : [], [scheduleProp]);

  const handleExport = () => {
    exportAmortizationSchedule(schedule, contract);
  };

  const formatCurrency = (value, showSign = false) => {
    if (!value && value !== 0) return '-';
    const num = parseFloat(value);
    const formatted = formatNumber(Math.abs(num), dp);
    if (showSign && num < 0) {
      return `(${formatted})`;
    }
    return formatted;
  };

  // Format amount with flow direction — delegates to the shared formatter so the
  // schedule table, approval modal and comparison table render signs identically
  // (outflow red parens, inflow blue, negative accrual/neutral -> default parens).
  const formatFlowAmount = (value, flowType) => formatNarrationAmount(value, flowType);

  // Render WHT amount for a row. Accrual rows render WHT as a plain (signed)
  // number. Payment rows render WHT in purple parens to indicate cash outflow
  // to the tax authority. The gross-up indicator (GU/NG/MX) is rendered in a
  // separate WHT Mode column, not here.
  const formatWhtForRow = (whtAmount, narration) => {
    const wht = parseFloat(whtAmount) || 0;
    if (wht === 0) return '-';
    // T064: Interest Float rows are accrual-only sub-rows emitted between
    // rate changes inside a combined-payment period; treat like Interest Accrual.
    // Interest Accrual Adjustment (was 'Carried Interest Accrual') is also an accrual —
    // render it as a plain accrual figure, not payment-style purple parens.
    // isAdjustmentNarration matches both labels for pre-rename approval snapshots.
    if (narration === 'Interest Accrual' || narration === 'Interest Float' || isAdjustmentNarration(narration)) {
      // Positive -> plain; negative (an Adjustment WHT credit-back) -> default-colour
      // accounting parentheses (21.25), matching formatNarrationAmount's convention.
      if (wht < 0) return `(${formatNumber(Math.abs(wht), 2)})`;
      return formatNumber(wht, 2);
    }
    return <span style={{ color: '#9c27b0' }}>({formatNumber(wht, 2)})</span>;
  };

  // Render the WHT Mode cell content for a row. Returns a GU / NG / MX label
  // with tooltip for any row that has WHT > 0 (accrual or payment); '-' otherwise.
  // narration is kept in the signature for call-site symmetry but is not used.
  // eslint-disable-next-line no-unused-vars
  const renderWhtMode = (grossUp, narration, whtAmount) => {
    if (!(Math.abs(parseFloat(whtAmount) || 0) > 0)) return '-';
    const label = grossUp === 'mixed' ? 'MX' : grossUp === true ? 'GU' : grossUp === false ? 'NG' : '-';
    if (label === '-') return '-';
    const tooltip = grossUp === 'mixed'
      ? 'Mixed gross-up treatment across investors'
      : grossUp === true
        ? 'Grossed up — WHT paid by company on top of interest'
        : "Not grossed up — WHT deducted from investor's interest";
    return (
      <Tooltip title={tooltip} arrow>
        <Box component="span" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{label}</Box>
      </Tooltip>
    );
  };

  // getFlowType is the shared getNarrationFlowType from utils/narration (imported above),
  // so the table, the approval modal, and the narration chip colouring classify rows identically.

  // getNarrationColor / getNarrationChipProps now come from utils/narration so the
  // rate-change comparison table renders identical narration chips.

  // Shared flow-chip rule (IN/OUT/ACC/ADJ). ADJ returns an sx (maroon) instead of a
  // semantic color; render sites spread flowLabel.sx so it applies.
  const getFlowLabel = getNarrationFlowLabel;

  // Multi-investor detection + period grouping come from the shared util so the wizard
  // and the approval modal present identical grouped totals (financial consistency).
  const { isMultiInvestor, groups: groupedScheduleFromUtil } = useMemo(
    () => groupAmortizationSchedule(schedule),
    [schedule]
  );

  // Per-investor WHT breakdown for multi-investor schedules
  const investorWhtSummary = useMemo(() => {
    if (!isMultiInvestor) return [];
    const map = {};
    schedule.forEach(entry => {
      const id = entry.investor_id;
      if (id == null) return;
      if (!map[id]) {
        map[id] = {
          investor_id: id,
          investor_name: entry.investor_name || String(id),
          capital_amount: 0,
          gross_interest: 0,
          wht_total: 0,
          net_interest: 0,
          wht_rate: parseFloat(entry.wht_rate_applied) || 0,
        };
      }
      const flowType = getFlowType(entry.narration);
      if (flowType === 'inflow') {
        map[id].capital_amount += parseFloat(entry.capital_amount) || 0;
      }
      if (flowType === 'outflow') {
        const gross = parseFloat(entry.gross_interest_amount) || parseFloat(entry.interest_amount) || 0;
        const wht = parseFloat(entry.wht_amount) || 0;
        const net = parseFloat(entry.net_interest_amount) || parseFloat(entry.interest_amount) || 0;
        map[id].gross_interest += gross;
        map[id].wht_total += wht;
        map[id].net_interest += net;
      }
    });
    return Object.values(map);
  }, [isMultiInvestor, schedule]);

  // Show the WHT Mode column only when at least one row has non-zero WHT
  // (abs, so a negative carried-accrual WHT still surfaces the column).
  const showWhtMode = useMemo(
    () => (schedule || []).some(e => Math.abs(parseFloat(e.wht_amount) || 0) > 0),
    [schedule]
  );

  // Precompute running balance: previous balance + current row's total
  // Inflows (capital drawdown, injection) add; outflows (payments, withdrawals) subtract; accruals add
  // For outflows, use gross_interest_amount to symmetrically match what accruals add.
  // This correctly handles both gross-up (where net=gross, so net+wht > gross) and
  // non-gross-up (where net+wht = gross) WHT scenarios.
  const runningBalances = useMemo(() => {
    if (!schedule || schedule.length === 0) return [];
    const balances = [];
    let balance = 0;
    schedule.forEach((entry) => {
      const capital = parseFloat(entry.capital_amount) || 0;
      const interest = parseFloat(entry.interest_amount) || 0;
      const wht = parseFloat(entry.wht_amount) || 0;
      const total = capital + interest + wht;
      const flowType = getFlowType(entry.narration);

      if (flowType === 'inflow') {
        balance += total;
      } else if (flowType === 'outflow') {
        // Use gross_interest_amount for symmetric treatment with accruals.
        // Accruals add gross interest; payments must subtract gross interest.
        // For non-gross-up: gross = net + wht (same result as before).
        // For gross-up: net = gross and wht > 0, so (net + wht) would over-subtract.
        const grossInterest = parseFloat(entry.gross_interest_amount) || (interest + wht);
        balance -= (capital + grossInterest);
      } else if (flowType === 'accrual') {
        // Accrual rows carry interest_amount = gross_interest_amount and a separate
        // wht_amount for display. Adding `total` (= interest + wht) here would
        // double-count WHT, leaving it stranded after the matching payment subtracts
        // only gross. Mirror the outflow branch: add gross interest, not interest+wht.
        const grossInterest = parseFloat(entry.gross_interest_amount) || interest;
        balance += (capital + grossInterest);
      }

      balances.push(Math.round(balance * 100) / 100);
    });
    return balances;
  }, [schedule]);

  // Grouped multi-investor periods (payment_date + narration), from the shared util.
  const groupedSchedule = groupedScheduleFromUtil;

  // Effective page size for slicing. MUI's "All" option uses value -1; when
  // selected we render every row in one continuous list (handy for reconciling a
  // full schedule against a statement). Row count = grouped length when grouping
  // (multi-investor) else the flat schedule length.
  const totalRowCount = groupedSchedule ? groupedSchedule.length : schedule.length;
  // showAllRows (wizard / preview contexts) renders the whole schedule with no
  // pager: page 0, page size = every row. Otherwise use the shared pagination
  // hook (page size -1 is its "All" option, same as before).
  const page = showAllRows ? 0 : pagination.page;
  const effectivePageSize = showAllRows
    ? Math.max(totalRowCount, 1)
    : (pagination.rowsPerPage === -1 ? Math.max(totalRowCount, 1) : pagination.rowsPerPage);

  const handleExpandAllGroups = () => {
    if (!groupedSchedule) return;
    const allKeys = {};
    groupedSchedule.forEach(g => { allKeys[g.groupKey] = true; });
    setExpandedGroups(allKeys);
  };

  const handleCollapseAllGroups = () => {
    setExpandedGroups({});
  };

  if (!schedule || schedule.length === 0) {
    const getEmptyMessage = () => {
      switch (contract?.status) {
        case 'Draft':
          return 'Generate a preview of the amortization schedule for review.';
        case 'Pending Approval':
          return 'Generate or regenerate the schedule for approval review.';
        case 'Approved':
          return 'Activate the contract to generate the schedule, or generate it manually below.';
        case 'Active':
          return 'Click below to generate the amortization schedule.';
        default:
          return 'Schedule will be generated when contract is activated.';
      }
    };

    // Allow generation for Draft, Pending Approval, Approved, and Active statuses
    const canGenerate = ['Draft', 'Pending Approval', 'Approved', 'Active'].includes(contract?.status);

    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography color="textSecondary" gutterBottom>
            No amortization schedule available.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {getEmptyMessage()}
          </Typography>
          {canGenerate && onRegenerate && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={onRegenerate}
            >
              Generate Schedule
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  // Resolve currency label. Prop wins because some callers (wizard preview) build
  // the contract object from form state, which has investment_currency_id but not
  // the join-derived currency_code. Fallback to contract.currency_code for loaded
  // contracts (ContractDetail page, summary tab, etc.).
  const ccy = currencyCode || contract.currency_code || '';
  // Prefix that renders "SZL " before an amount, or nothing when no currency is known.
  // Used in JSX (e.g. <>{ccyPrefix}{formatNumber(...)}</>).
  const ccyPrefix = ccy ? `${ccy} ` : '';

  // T117: multi-currency contracts settle in a different currency than they accrue in.
  // fn_amortization_get_schedule returns settlement_currency_code / fx_rate /
  // equivalent_* only for those contracts (NULL otherwise), so presence on any row is
  // the gate. The equivalent columns show the schedule converted into the settlement
  // currency (dealt rate on matched rows, live indicative rate on unmatched rows).
  const settlementCcy = schedule.find(e => e.settlement_currency_code)?.settlement_currency_code
    || contract?.settlement_currency_code || '';
  const isMultiCurrency = !!settlementCcy && schedule.some(e => e.settlement_currency_code);

  // Render the 3 settlement-currency cells for a row. The equivalents are derived from the
  // amounts the table ALREADY shows (contract-currency payment + running balance) times the
  // row's rate, so the converted figures stay consistent with the canonical columns rather
  // than risking drift against a separately-computed DB value. `contractBalance` is the
  // running balance the table displays for this row (frontend-computed, flow-aware).
  const renderSettlementCells = (entry, contractBalance) => {
    if (!isMultiCurrency) return null;
    const rate = parseFloat(entry?.fx_rate);
    if (!Number.isFinite(rate) || rate <= 0) {
      // No rate available (e.g. unmatched row with no live FX rate configured).
      return (
        <>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">-</TableCell>
        </>
      );
    }
    const payment = parseFloat(entry?.payment_amount) || 0;
    const hasBalance = contractBalance != null && Number.isFinite(parseFloat(contractBalance));
    const balance = parseFloat(contractBalance) || 0;
    // fx_rate is settlement->contract (settlement * rate = contract), so a CONTRACT-currency
    // amount expressed in the SETTLEMENT currency is amount / rate. The settlement currency is
    // already shown in the column headers, so the cells omit the prefix.
    return (
      <>
        <TableCell align="right">{formatNumber(rate, 6)}</TableCell>
        <TableCell align="right">{payment === 0 ? '-' : formatNumber(payment / rate, dp)}</TableCell>
        <TableCell align="right">{hasBalance ? formatNumber(balance / rate, dp) : '-'}</TableCell>
      </>
    );
  };

  // Check if schedule has WHT data. Gate on a non-zero WHT amount on at least
  // one row — wht_rate_applied gets stored even when the rate is 0, so we
  // can't use it as a presence signal. Use abs so a schedule whose only WHT is a
  // negative carried-accrual credit still shows the WHT column.
  const hasWhtData = schedule.some(entry => Math.abs(parseFloat(entry.wht_amount) || 0) > 0);

  // Check if any entries use gross-up (WHT is company expense, not deducted from investor)
  const hasGrossUp = schedule.some(entry => entry.wht_gross_up);

  // Extract WHT rate from schedule entries (first entry with a rate)
  const whtRateEntry = schedule.find(entry => entry.wht_rate_applied && parseFloat(entry.wht_rate_applied) > 0);
  const whtRate = whtRateEntry ? parseFloat(whtRateEntry.wht_rate_applied) : 0;
  const whtRateSource = whtRateEntry ? whtRateEntry.wht_rate_source : null;

  // Per-period overrides on Draft and Approved. Active contracts are altered
  // via Capital Amendment / Interest Payment / Finalisation flows. Pending
  // Approval is locked (approver's hands).
  const canOverride = canAdmin && onOverrideRequest && !readOnly
    && ['Draft', 'Approved'].includes(contract?.status);

  // Per-row overrides apply to payment rows (Interest Payment / Combined
  // Payment / Capital Payment). T108: editing happens on the payment line — an
  // interest edit is apportioned back across the period's accruals; there is no
  // longer a direct per-accrual edit icon. Interest is editable on Interest /
  // Combined Payment; capital on Combined / Capital Payment.
  const isOverridable = (narration) =>
    narration === 'Interest Payment' || narration === 'Combined Payment' || narration === 'Capital Payment';

  // Calculate schedule totals with inflow/outflow separation
  const totals = schedule.reduce((acc, entry) => {
    const flowType = getFlowType(entry.narration);
    const capitalAmt = parseFloat(entry.capital_amount) || 0;
    const interestAmt = parseFloat(entry.interest_amount) || 0;
    const grossInterestAmt = parseFloat(entry.gross_interest_amount) || interestAmt;
    const whtAmt = parseFloat(entry.wht_amount) || 0;
    const netInterestAmt = parseFloat(entry.net_interest_amount) || interestAmt;
    const paymentAmt = parseFloat(entry.payment_amount) || 0;

    if (flowType === 'inflow') {
      acc.capitalInflow += capitalAmt;
    } else if (flowType === 'outflow') {
      acc.capitalOutflow += capitalAmt;
      acc.interestOutflow += interestAmt;
      acc.grossInterestOutflow += grossInterestAmt;
      acc.whtTotal += whtAmt;
      // Split WHT by gross-up treatment (per-row wht_gross_up): grossed-up = company expense
      // (paid on top, net = gross); non-grossed-up = deducted from the investor (net = gross − wht).
      // A mixed contract has both — the cards render them separately so the total reconciles.
      if (entry.wht_gross_up === true || entry.wht_gross_up === 'true') acc.whtCompany += whtAmt;
      else acc.whtDeducted += whtAmt;
      acc.netInterestOutflow += netInterestAmt;
    }
    acc.totalPayments += paymentAmt;
    return acc;
  }, { capitalInflow: 0, capitalOutflow: 0, interestOutflow: 0, grossInterestOutflow: 0, whtTotal: 0, whtDeducted: 0, whtCompany: 0, netInterestOutflow: 0, totalPayments: 0 });

  // Mixed = both a deducted and a company-expense WHT portion exist (some investors grossed up, some not).
  const isMixedWht = totals.whtDeducted > 0 && totals.whtCompany > 0;

  return (
    <Box>
      {/* Contract Inputs Summary */}
      <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: paramsExpanded ? 2 : 0,
              cursor: 'pointer',
              px: 2,
              py: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => setParamsExpanded(prev => !prev)}
          >
            <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" color="primary" sx={{ flexGrow: 1 }}>
              Schedule Parameters
            </Typography>
            <IconButton size="small" aria-label={paramsExpanded ? 'Collapse parameters' : 'Expand parameters'}>
              {paramsExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Box>
          <Collapse in={paramsExpanded} timeout="auto" unmountOnExit>
          <Grid container spacing={2}>
            {/* Investment Details */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Investment Details
              </Typography>
              <Grid container spacing={1}>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Capital Amount</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {ccyPrefix}{formatNumber(contract.capital_amount, 2)}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Initial Interest Rate</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {contract.initial_interest_rate ?? contract.interest_rate}% {formatRateType(contract.rate_type)}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Current Interest Rate</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {contract.interest_rate}% {formatRateType(contract.rate_type)}
                  </Typography>
                </Grid>
                {(contract.rate_type === 'Variable' || contract.rate_type === 'Floating') && contract.benchmark_rate_type && (
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Spread</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {contract.benchmark_rate_type}: {contract.benchmark_rate_value}%
                      {parseFloat(contract.rate_spread || 0) !== 0 && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}({parseFloat(contract.rate_spread) >= 0 ? '+' : ''}{parseFloat(contract.rate_spread).toFixed(2)}% spread)
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                )}
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Term</Typography>
                  <Typography variant="body2">
                    {contract.investment_term_value} {contract.investment_term}
                    {contract.deposit_date && contract.maturity_date && (() => {
                      const start = new Date(contract.deposit_date);
                      const end = new Date(contract.maturity_date);
                      const totalDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
                      const basis = parseInt(contract.day_count_basis) || 365;
                      const years = (totalDays / basis).toFixed(2);
                      return <Typography variant="caption" color="textSecondary" component="span"> ({years} years / {totalDays} days)</Typography>;
                    })()}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Calculation Type</Typography>
                  <Typography variant="body2">
                    {contract.calculation_profile_interest_method
                      ? `${contract.calculation_profile_interest_method} Interest`
                      : contract.calculation_profile_name || 'Simple Interest'}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Day Count Basis</Typography>
                  <Typography variant="body2">
                    {contract.day_count_basis === '360' || contract.day_count_basis === 360
                      ? '360 days (Actual/360)'
                      : '365 days (Actual/365)'}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Interest Formula</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', backgroundColor: '#f5f5f5', p: 0.5, borderRadius: 0.5 }}>
                    {contract.calculation_profile_formula
                      || ((contract.calculation_profile_interest_method || 'Simple') === 'Compound'
                        ? `Interest = Principal x (1 + Rate/${contract.calculation_profile_compounding_frequency || 12})^(${contract.calculation_profile_compounding_frequency || 12} x Actual Days / ${contract.day_count_basis || 365}) - Principal`
                        : (contract.calculation_profile_interest_method === 'Compound Linear'
                          ? `Daily Amount = (Principal x (1 + Rate)^(Term Days / ${contract.day_count_basis || 365}) - Principal) / Term Days; Period Interest = Daily Amount x Days in Period`
                          : (contract.calculation_profile_interest_method === 'Simple Periodic Capitalization'
                            ? `Within period: Interest = Principal x Rate x (Actual Days / ${contract.day_count_basis || 365}). Principal capitalizes (steps up by accrued interest) at each period boundary.`
                            : `Interest = Principal x Rate x (Actual Days / ${contract.day_count_basis || 365})`)))}
                  </Typography>
                </Grid>
                {hasWhtData && (
                  <>
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">Withholding Tax Rate</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {whtRate > 0 ? `${whtRate.toFixed(2)}%` : 'None'}
                        </Typography>
                        {whtRateSource === 'override' && (
                          <Chip label="Override" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                        {(whtRateSource === 'rule_country' || whtRateSource === 'rule_default') && (
                          <Chip
                            label={whtRateSource === 'rule_country' ? 'Rule (Country)' : 'Rule (Default)'}
                            size="small"
                            color="info"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {whtRateSource === 'investor_manual' && (
                          <Chip label="Manual" size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                        {whtRateSource === 'fallback' && (
                          <Chip label="Fallback" size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">Withholding Tax Gross-Up</Typography>
                      <Typography variant="body2">
                        <Chip
                          label={hasGrossUp ? 'Yes - Company Expense' : 'No - Deducted from Investor'}
                          size="small"
                          color={hasGrossUp ? 'info' : 'default'}
                          variant={hasGrossUp ? 'filled' : 'outlined'}
                          sx={{ height: 22 }}
                        />
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>

            {/* Dates */}
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Key Dates
              </Typography>
              <Grid container spacing={1}>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Start Date</Typography>
                  <Typography variant="body2">
                    {contract.deposit_date ? format(new Date(contract.deposit_date), 'dd MMM yyyy') : '-'}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Funds Received</Typography>
                  <Typography variant="body2">
                    {contract.funds_received_date
                      ? format(new Date(contract.funds_received_date), 'dd MMM yyyy')
                      : 'Same as deposit'}
                    {contract.funds_received_date && contract.funds_received_date !== contract.deposit_date && (
                      <Chip label="Interest start" size="small" color="info" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Maturity Date</Typography>
                  <Typography variant="body2">
                    {contract.maturity_date ? format(new Date(contract.maturity_date), 'dd MMM yyyy') : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Payment Frequencies */}
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Payment Frequencies
              </Typography>
              <Grid container spacing={1}>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Interest Paid</Typography>
                  <Typography variant="body2">
                    <Chip
                      label={contract.interest_paid_frequency || 'Monthly'}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ height: 22 }}
                    />
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Capital Repaid</Typography>
                  <Typography variant="body2">
                    <Chip
                      label={contract.capital_paid_frequency || 'At Maturity'}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 22 }}
                    />
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          </Collapse>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mt: 1,
              mb: cashFlowExpanded ? 2 : 0,
              px: 2,
              py: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => setCashFlowExpanded(prev => !prev)}
          >
            <WalletIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" color="primary" sx={{ flexGrow: 1 }}>
              Cash Flow Summary
            </Typography>
            <IconButton size="small" aria-label={cashFlowExpanded ? 'Collapse cash flow summary' : 'Expand cash flow summary'}>
              {cashFlowExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Box>
          <Collapse in={cashFlowExpanded} timeout="auto" unmountOnExit>
          <Divider sx={{ mb: 2 }} />

          {/* Schedule Totals */}
          {/* Cash flow cards — single flex row so any count (4 to 7 cards across
              WHT / non-WHT / with-variance / no-variance combinations) shares
              the row equally without wrapping. */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap' }}>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
              <Typography variant="caption" color="textSecondary">Capital Inflow</Typography>
              <Typography variant="body1" fontWeight="bold" color="info.main" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                + {ccyPrefix}{formatNumber(totals.capitalInflow, 2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">From Investor</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #ef9a9a' }}>
              <Typography variant="caption" color="textSecondary">Capital Outflow</Typography>
              <Typography variant="body1" fontWeight="bold" color="error.main" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                - {ccyPrefix}{formatNumber(totals.capitalOutflow, 2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">To Investor</Typography>
            </Box>
            {hasWhtData ? (
              <>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
                  <Typography variant="caption" color="textSecondary">Gross Interest</Typography>
                  <Typography variant="body1" fontWeight="bold" color="warning.dark" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    - {ccyPrefix}{formatNumber(totals.grossInterestOutflow, 2)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Before Withholding Tax</Typography>
                </Box>
                {isMixedWht ? (
                  // Mixed gross-up: split WHT into the deducted-from-investor portion and the
                  // company-expense (grossed-up) portion so the total is honest and reconciles.
                  <>
                    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#f3e5f5', borderRadius: 1, border: '1px solid #ce93d8' }}>
                      <Typography variant="caption" color="textSecondary">Withholding Tax</Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ color: '#9c27b0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ccyPrefix}{formatNumber(totals.whtDeducted, 2)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">Deducted from Investor</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#f3e5f5', borderRadius: 1, border: '1px solid #ce93d8' }}>
                      <Typography variant="caption" color="textSecondary">Withholding Tax</Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ color: '#9c27b0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ccyPrefix}{formatNumber(totals.whtCompany, 2)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">Company Expense (Not Deducted)</Typography>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#f3e5f5', borderRadius: 1, border: '1px solid #ce93d8' }}>
                    <Typography variant="caption" color="textSecondary">{hasGrossUp ? 'Withholding Tax (Company Expense)' : 'Withholding Tax'}</Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: '#9c27b0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {ccyPrefix}{formatNumber(totals.whtTotal, 2)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">{hasGrossUp ? 'Not Deducted' : 'Tax Withheld'}</Typography>
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#e8f5e9', borderRadius: 1, border: '1px solid #a5d6a7' }}>
                  <Typography variant="caption" color="textSecondary">Net Interest</Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    - {ccyPrefix}{formatNumber(totals.netInterestOutflow, 2)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">To Investor</Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
                <Typography variant="caption" color="textSecondary">Interest Paid</Typography>
                <Typography variant="body1" fontWeight="bold" color="warning.dark" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  - {ccyPrefix}{formatNumber(totals.interestOutflow, 2)}
                </Typography>
                <Typography variant="caption" color="textSecondary">Return to Investor</Typography>
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="textSecondary">Net Flow</Typography>
              <Typography variant="body1" fontWeight="bold" color={totals.capitalInflow - totals.capitalOutflow - totals.interestOutflow >= 0 ? 'success.main' : 'error.main'} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {ccyPrefix}{formatNumber(totals.capitalInflow - totals.capitalOutflow - totals.interestOutflow, 2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">At End of Term</Typography>
            </Box>
            {(() => {
              // Cumulative variance card — render only when there's any
              // recorded variance against this contract.
              if (!variances || variances.size === 0) return null;
              let net = 0;
              let count = 0;
              variances.forEach((v) => {
                const amt = parseFloat(v?.variance_amount);
                if (Number.isFinite(amt) && Math.abs(amt) >= 0.01) {
                  net += amt;
                  count += 1;
                }
              });
              if (count === 0) return null;
              const isShort = net < 0;
              return (
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: isShort ? '#ffebee' : '#e8f5e9', borderRadius: 1, border: `1px solid ${isShort ? '#ef9a9a' : '#a5d6a7'}` }}>
                  <Typography variant="caption" color="textSecondary">Cumulative Variance</Typography>
                  <Typography variant="body1" fontWeight="bold" color={isShort ? 'error.main' : 'success.main'} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {isShort ? '-' : '+'} {ccyPrefix}{formatNumber(Math.abs(net), 2)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {count} {count === 1 ? 'varied payment' : 'varied payments'}
                  </Typography>
                </Box>
              );
            })()}
          </Box>
          {isMultiInvestor && investorWhtSummary.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Per-Investor WHT Breakdown
                </Typography>
                <TableContainer component={Box}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 1, fontSize: '0.78rem' } }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>Investor</strong></TableCell>
                        <TableCell align="right"><strong>Capital</strong></TableCell>
                        <TableCell align="right"><strong>WHT Rate</strong></TableCell>
                        <TableCell align="right"><strong>Gross Interest</strong></TableCell>
                        <TableCell align="right"><strong>WHT</strong></TableCell>
                        <TableCell align="right"><strong>Net Interest</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {investorWhtSummary.map((inv) => (
                        <TableRow key={inv.investor_id} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                          <TableCell>{inv.investor_name}</TableCell>
                          <TableCell align="right">{ccyPrefix}{formatNumber(inv.capital_amount, 2)}</TableCell>
                          <TableCell align="right">{inv.wht_rate > 0 ? `${inv.wht_rate.toFixed(2)}%` : '—'}</TableCell>
                          <TableCell align="right">{ccyPrefix}{formatNumber(inv.gross_interest, 2)}</TableCell>
                          <TableCell align="right" sx={{ color: '#9c27b0' }}>
                            {inv.wht_total > 0 ? <>({ccyPrefix}{formatNumber(inv.wht_total, 2)})</> : '—'}
                          </TableCell>
                          <TableCell align="right">{ccyPrefix}{formatNumber(inv.net_interest, 2)}</TableCell>
                        </TableRow>
                      ))}
                      {investorWhtSummary.length > 1 && (
                        <TableRow sx={{ backgroundColor: '#f0f4ff', fontWeight: 'bold' }}>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell align="right">
                            <strong>{ccyPrefix}{formatNumber(investorWhtSummary.reduce((s, i) => s + i.capital_amount, 0), 2)}</strong>
                          </TableCell>
                          <TableCell align="right">—</TableCell>
                          <TableCell align="right">
                            <strong>{ccyPrefix}{formatNumber(investorWhtSummary.reduce((s, i) => s + i.gross_interest, 0), 2)}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#9c27b0' }}>
                            <strong>({ccyPrefix}{formatNumber(investorWhtSummary.reduce((s, i) => s + i.wht_total, 0), 2)})</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>{ccyPrefix}{formatNumber(investorWhtSummary.reduce((s, i) => s + i.net_interest, 0), 2)}</strong>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
          </Collapse>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            Amortization Schedule ({groupedSchedule
              ? `${groupedSchedule.length} periods, ${schedule.length} entries`
              : `${schedule.length} entries`})
          </Typography>
          {groupedSchedule && (
            <>
              <Tooltip title="Expand all groups">
                <IconButton size="small" onClick={handleExpandAllGroups}>
                  <ExpandAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Collapse all groups">
                <IconButton size="small" onClick={handleCollapseAllGroups}>
                  <CollapseAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onDownloadPDF && (
            <Button
              variant="outlined"
              size="small"
              startIcon={isGeneratingPDF ? <CircularProgress size={16} /> : <PdfIcon />}
              onClick={onDownloadPDF}
              color="error"
              disabled={isGeneratingPDF}
            >
              {isGeneratingPDF ? 'Generating...' : 'Download Schedule PDF'}
            </Button>
          )}
          {!previewMode && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
            >
              Export to Excel
            </Button>
          )}
          {!previewMode && canAdmin && onRegenerate && !readOnly && !hideRegenerateButton && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRegenerate}
              color="warning"
            >
              Regenerate
            </Button>
          )}
        </Box>
      </Box>
      <TableContainer component={Paper}>
        {/* Keep every cell on one line; the container scrolls horizontally when the
            multi-currency columns push the table past the viewport (no text wrapping). */}
        <Table size="small" sx={{ minWidth: 1200, '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {groupedSchedule && <TableCell sx={{ width: 40, p: 0.5 }}></TableCell>}
              <TableCell>Seq</TableCell>
              {!previewMode && <TableCell align="center">Version</TableCell>}
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Payment Date</TableCell>
              {isMultiInvestor && <TableCell>Investor</TableCell>}
              <TableCell align="center">Flow</TableCell>
              <TableCell>Narration</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Rate %</TableCell>
              <TableCell align="right">Days</TableCell>
              <TableCell align="right">Capital Amount</TableCell>
              <TableCell align="right">Interest Amount</TableCell>
              {hasWhtData && <TableCell align="right">Withholding Tax</TableCell>}
              {showWhtMode && <TableCell align="center">WHT Mode</TableCell>}
              <TableCell align="right">Payment Amount</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Outstanding Balance</TableCell>
              {/* T117: settlement-currency equivalents — only for multi-currency contracts */}
              {isMultiCurrency && <TableCell align="right">FX Rate</TableCell>}
              {isMultiCurrency && <TableCell align="right">Equiv. Payment ({settlementCcy})</TableCell>}
              {isMultiCurrency && <TableCell align="right">Equiv. Balance ({settlementCcy})</TableCell>}
              {canOverride && <TableCell align="center" sx={{ width: 40 }}></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedSchedule ? (
              /* Grouped multi-investor rendering */
              (groupedSchedule
                .slice(page * effectivePageSize, page * effectivePageSize + effectivePageSize)
                .map((group, groupIndex) => {
                  const flowType = getFlowType(group.narration);
                  const flowLabel = getFlowLabel(group.narration);
                  const isExpanded = !!expandedGroups[group.groupKey];
                  const isSingleEntry = group.investorCount === 1;
                  // Total = investor take-home: capital + net_interest_amount.
                  // For GU rows net == gross interest (no deduction). For NG rows
                  // net == gross − wht. For mixed groups this sums per-investor
                  // take-homes (net_interest_amount is summed in the reducer above).
                  const groupTotal = group.capital_amount + (group.net_interest_amount || 0);

                  return (
                    <React.Fragment key={group.groupKey}>
                      {/* Summary row */}
                      <TableRow
                        hover
                        sx={{
                          cursor: isSingleEntry ? 'default' : 'pointer',
                          // Row band resolved from type (override > adjustment > group header).
                          ...getNarrationRowSx(group.narration, { hasOverride: group.hasAnyOverride, isGroup: true }),
                        }}
                        onClick={isSingleEntry ? undefined : () => toggleGroup(group.groupKey)}
                      >
                        <TableCell sx={{ width: 40, p: 0.5 }}>
                          {!isSingleEntry && (
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell><strong>{page * effectivePageSize + groupIndex + 1}</strong></TableCell>
                        {!previewMode && (
                          <TableCell align="center">
                            {(() => {
                              const v = versionChipProps(group.version_number, group.version_type);
                              return v
                                ? <Chip label={v.label} title={v.title} size="small" color={v.color} variant="outlined" />
                                : '-';
                            })()}
                          </TableCell>
                        )}
                        <TableCell>{formatShortDate(group.period_start)}</TableCell>
                        <TableCell>{formatShortDate(group.period_end)}</TableCell>
                        <TableCell>
                          {group.payment_date ? format(new Date(group.payment_date), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          {isSingleEntry ? (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {group.entries[0].investor_name || '-'}
                            </Typography>
                          ) : (
                            <Chip
                              label={`${group.investorCount} investors`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={flowLabel.label} size="small" color={flowLabel.color} variant={flowLabel.sx ? 'outlined' : undefined} sx={{ minWidth: 40, fontWeight: 'bold', ...(flowLabel.sx || {}) }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={group.narration || 'N/A'} size="small" {...getNarrationChipProps(group.narration)} />
                        </TableCell>
                        <TableCell align="center">
                          {(() => {
                            const varianceChip = group.entries?.length === 1 ? renderVarianceChip(group.entries[0]?.id) : null;
                            if (!group.status && !varianceChip) {
                              return <Typography variant="caption" color="textSecondary">-</Typography>;
                            }
                            return (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {group.status && (
                                  <Chip
                                    label={group.status}
                                    size="small"
                                    color={group.status === 'Paid' || group.status === 'Received' ? 'success' : group.status === 'Pending' ? 'warning' : 'default'}
                                    variant={group.status === 'Pending' ? 'outlined' : 'filled'}
                                    sx={{ minWidth: 70 }}
                                  />
                                )}
                                {varianceChip}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        {/* A rate belongs to a period that ACCRUES. Payment rows (Interest/Capital/
                            Combined Payment) settle a balance and carry no rate of their own, so they
                            show a dash — matching the Days column, which is already accrual-only in
                            ScheduleComparisonTable. The engine still stamps interest_rate on those rows
                            (see emitInterestPayment) and it stays in the DB for audit; this is display
                            only. Note the 'Mixed' branch must be gated too, or an OUT row on a
                            multi-investor contract still renders 'Mixed'. */}
                        <TableCell align="right">
                          {!isPeriodAccrual(group.narration) ? '-' : group._ratesDiffer ? (
                            <Tooltip title="Rates differ across investors - expand to see details" arrow>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', cursor: 'help' }}>Mixed</Typography>
                            </Tooltip>
                          ) : (
                            group.interest_rate ? parseFloat(group.interest_rate).toFixed(2) : '-'
                          )}
                        </TableCell>
                        <TableCell align="right">{group.days_in_period || '-'}</TableCell>
                        <TableCell align="right">
                          <strong>{formatFlowAmount(group.capital_amount, flowType)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatFlowAmount(group.interest_amount, flowType)}</strong>
                          {group.hasAnyOverride && (
                            <Chip label="Override" size="small" color="warning" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                          )}
                        </TableCell>
                        {hasWhtData && (
                          <TableCell align="right">
                            {group.wht_amount && Math.abs(group.wht_amount) > 0
                              ? formatWhtForRow(group.wht_amount, group.narration)
                              : '-'}
                          </TableCell>
                        )}
                        {showWhtMode && (
                          <TableCell align="center">
                            {renderWhtMode(
                              group._grossUpVaries ? 'mixed' : group.wht_gross_up,
                              group.narration,
                              Math.abs(group.wht_amount)
                            )}
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <strong>{formatFlowAmount(group.payment_amount, flowType)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          {groupTotal === 0 ? '-' : <strong>{formatFlowAmount(groupTotal, flowType)}</strong>}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(runningBalances[group.lastEntryIndex])}</strong>
                        </TableCell>
                        {/* Group summary uses the group's representative row for fx_rate; the
                            equivalent payment is the group's combined payment in settlement ccy. */}
                        {renderSettlementCells(
                          { fx_rate: group.entries?.[0]?.fx_rate, payment_amount: group.payment_amount },
                          runningBalances[group.lastEntryIndex]
                        )}
                        {canOverride && <TableCell />}
                      </TableRow>

                      {/* Expanded detail rows per investor */}
                      {isExpanded && !isSingleEntry && group.entries.map((entry, entryIdx) => {
                        const entryFlowType = getFlowType(entry.narration);
                        const hasOverride = entry.has_override;
                        const overrideTooltip = hasOverride
                          ? `Overridden: original interest ${entry.original_interest_amount != null ? 'R ' + formatNumber(parseFloat(entry.original_interest_amount), 2) : ''}`
                          : '';

                        return (
                          <TableRow
                            key={entry.id || `${group.groupKey}-${entryIdx}`}
                            sx={{
                              // Override band wins; otherwise the expanded sub-rows keep
                              // their own (lighter) striping shades. (Adjustment rows are
                              // marked by their maroon chips, not a row band.)
                              backgroundColor: hasOverride
                                ? NARRATION_ROW_STYLES.override.bg
                                : (entryIdx % 2 === 0 ? '#fafcff' : '#ffffff'),
                              borderLeft: '3px solid #1976d2',
                              '& .MuiTableCell-root': { py: 0.5, fontSize: '0.8rem' },
                            }}
                          >
                            <TableCell sx={{ width: 40 }} />
                            <TableCell />
                            {!previewMode && <TableCell />}
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, pl: 0.5 }}>
                                {entry.investor_name || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell align="center">
                              {(() => {
                                const varianceChip = renderVarianceChip(entry.id);
                                if (!entry.status && !varianceChip) {
                                  return <Typography variant="caption" color="textSecondary">-</Typography>;
                                }
                                return (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                    {entry.status && (
                                      <Chip
                                        label={entry.status}
                                        size="small"
                                        color={entry.status === 'Paid' || entry.status === 'Received' ? 'success' : entry.status === 'Pending' ? 'warning' : 'default'}
                                        variant={entry.status === 'Pending' ? 'outlined' : 'filled'}
                                        sx={{ minWidth: 60, height: 20, fontSize: '0.7rem' }}
                                      />
                                    )}
                                    {varianceChip}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            {/* Accrual rows only — see the group row above. */}
                            <TableCell align="right">
                              {isPeriodAccrual(entry.narration) && entry.interest_rate
                                ? parseFloat(entry.interest_rate).toFixed(2)
                                : '-'}
                            </TableCell>
                            <TableCell />
                            <TableCell align="right">
                              {formatFlowAmount(entry.capital_amount, entryFlowType)}
                            </TableCell>
                            <TableCell align="right">
                              {hasOverride && entry.original_interest_amount != null ? (
                                <Tooltip title={overrideTooltip} arrow>
                                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    {formatFlowAmount(entry.interest_amount, entryFlowType)}
                                    <Chip label="Override" size="small" color="warning" sx={{ height: 14, fontSize: '0.55rem' }} />
                                  </Box>
                                </Tooltip>
                              ) : (
                                // Σ calc-detail icon sits at the START of the cell (left), the amount
                                // stays right-aligned, so amounts line up across icon / no-icon rows.
                                <Box component="span" sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 0.3 }}>
                                  <Box component="span" sx={{ display: 'inline-flex', width: 14, flexShrink: 0 }}>
                                    {(entry.id || entry.calc_detail) && entry.narration === 'Interest Accrual' && (
                                      <Tooltip title="View calculation detail" arrow>
                                        <FunctionsIcon
                                          sx={{ fontSize: 12, color: 'action.disabled', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                          onClick={(e) => { e.stopPropagation(); handleAuditClick(e, entry); }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Box component="span">{formatFlowAmount(entry.interest_amount, entryFlowType)}</Box>
                                </Box>
                              )}
                            </TableCell>
                            {hasWhtData && (
                              <TableCell align="right">
                                {formatWhtForRow(entry.wht_amount, entry.narration)}
                              </TableCell>
                            )}
                            {showWhtMode && (
                              <TableCell align="center">
                                {renderWhtMode(entry.wht_gross_up, entry.narration, entry.wht_amount)}
                              </TableCell>
                            )}
                            <TableCell align="right">
                              {formatFlowAmount(entry.payment_amount, entryFlowType)}
                            </TableCell>
                            <TableCell align="right">
                              {(() => {
                                // Total = investor take-home: capital + net_interest_amount.
                                // GU: net = gross interest. NG: net = gross − wht.
                                const capital = parseFloat(entry.capital_amount) || 0;
                                const net = parseFloat(entry.net_interest_amount);
                                const netSafe = Number.isFinite(net) ? net : (parseFloat(entry.interest_amount) || 0);
                                const total = capital + netSafe;
                                if (total === 0) return '-';
                                return formatFlowAmount(total, entryFlowType);
                              })()}
                            </TableCell>
                            <TableCell />
                            {/* per-investor detail rows: show rate + equiv payment, no per-investor balance */}
                            {renderSettlementCells(entry, null)}
                            {canOverride && (
                              <TableCell align="center" sx={{ p: 0 }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {entry.has_override && onShowOverrides && (
                                    <Tooltip title="View / revert overrides on this row" arrow>
                                      <IconButton
                                        size="small"
                                        color="warning"
                                        onClick={(e) => { e.stopPropagation(); onShowOverrides(entry); }}
                                        sx={{ p: 0.25 }}
                                      >
                                        <FlagIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {isOverridable(entry.narration) && (
                                    <Tooltip title="Apply override" arrow>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => { e.stopPropagation(); onOverrideRequest(entry); }}
                                        sx={{ p: 0.25 }}
                                      >
                                        <EditIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                }))
            ) : (
              /* Flat single-investor rendering (unchanged) */
              (schedule
                .slice(page * effectivePageSize, page * effectivePageSize + effectivePageSize)
                .map((entry, index) => {
                  const flowType = getFlowType(entry.narration);
                  const flowLabel = getFlowLabel(entry.narration);

                  const hasOverride = entry.has_override;

                  return (
                    <TableRow
                      key={entry.id || index}
                      sx={getNarrationRowSx(entry.narration, { hasOverride })}
                    >
                      <TableCell>{page * effectivePageSize + index + 1}</TableCell>
                      {!previewMode && (
                        <TableCell align="center">
                          {(() => {
                            const v = versionChipProps(entry.version_number, entry.version_type);
                            return v ? (
                              <Chip
                                label={v.label}
                                size="small"
                                color={v.color}
                                variant="outlined"
                                // The row-level chip prefers the version's own reason, which is more
                                // specific than the generic label ("Finalised 2026-08-31").
                                title={entry.version_reason || v.title}
                              />
                            ) : '-';
                          })()}
                        </TableCell>
                      )}
                      <TableCell>{formatShortDate(entry.period_start)}</TableCell>
                      <TableCell>{formatShortDate(entry.period_end)}</TableCell>
                      <TableCell>
                        {entry.payment_date ? format(new Date(entry.payment_date), 'dd MMM yyyy') : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={flowLabel.label}
                          size="small"
                          color={flowLabel.color}
                          variant={flowLabel.sx ? 'outlined' : undefined}
                          sx={{ minWidth: 40, fontWeight: 'bold', ...(flowLabel.sx || {}) }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.narration || 'N/A'}
                          size="small"
                          {...getNarrationChipProps(entry.narration)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const varianceChip = renderVarianceChip(entry.id);
                          if (!entry.status && !varianceChip) {
                            return <Typography variant="caption" color="textSecondary">-</Typography>;
                          }
                          return (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                              {entry.status && (
                                <Chip
                                  label={entry.status}
                                  size="small"
                                  color={
                                    entry.status === 'Paid' || entry.status === 'Received' ? 'success' :
                                    entry.status === 'Pending' ? 'warning' : 'default'
                                  }
                                  variant={entry.status === 'Pending' ? 'outlined' : 'filled'}
                                  sx={{ minWidth: 70 }}
                                />
                              )}
                              {varianceChip}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      {/* Accrual rows only — see the grouped row above. */}
                      <TableCell align="right">
                        {isPeriodAccrual(entry.narration) && entry.interest_rate
                          ? parseFloat(entry.interest_rate).toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">{entry.days_in_period || '-'}</TableCell>
                      <TableCell align="right">
                        {hasOverride && entry.original_capital_amount != null ? (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Typography component="span" variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '0.75rem' }}>
                              {formatNumber(parseFloat(entry.original_capital_amount), 2)}
                            </Typography>
                            <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                              {formatFlowAmount(entry.capital_amount, flowType)}
                            </Typography>
                            {renderVarianceChipFor(entry, 'capital')}
                          </Box>
                        ) : (
                          formatFlowAmount(entry.capital_amount, flowType)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {hasOverride && entry.original_interest_amount != null ? (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Typography component="span" variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '0.75rem' }}>
                              {formatNumber(parseFloat(entry.original_interest_amount), 2)}
                            </Typography>
                            <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                              {formatFlowAmount(entry.interest_amount, flowType)}
                            </Typography>
                            {renderVarianceChipFor(entry, 'interest')}
                          </Box>
                        ) : (
                          // Residual chip + Σ calc-detail icon live at the START of the cell (left);
                          // the amount stays right-aligned so amounts line up across icon / no-icon rows.
                          <Box component="span" sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 0.3 }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}>
                              {parseFloat(entry.residual_carried_in || 0) !== 0 && (
                                <Tooltip title={`Includes R ${formatNumber(parseFloat(entry.residual_carried_in), 2)} rolled in from a prior override`} arrow>
                                  <Chip
                                    label={`${parseFloat(entry.residual_carried_in) >= 0 ? '+' : ''}R ${formatNumber(parseFloat(entry.residual_carried_in), 2)}`}
                                    size="small"
                                    color="info"
                                    sx={{ height: 16, fontSize: '0.6rem' }}
                                  />
                                </Tooltip>
                              )}
                              <Box component="span" sx={{ display: 'inline-flex', width: 16, flexShrink: 0 }}>
                                {(entry.id || entry.calc_detail) && entry.narration === 'Interest Accrual' && (
                                  <Tooltip title="View calculation detail" arrow>
                                    <FunctionsIcon
                                      sx={{ fontSize: 14, color: 'action.disabled', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                      onClick={(e) => handleAuditClick(e, entry)}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                            <Box component="span">{formatFlowAmount(entry.interest_amount, flowType)}</Box>
                          </Box>
                        )}
                      </TableCell>
                      {hasWhtData && (
                        <TableCell align="right">
                          {formatWhtForRow(entry.wht_amount, entry.narration)}
                        </TableCell>
                      )}
                      {showWhtMode && (
                        <TableCell align="center">
                          {renderWhtMode(entry.wht_gross_up, entry.narration, entry.wht_amount)}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <strong>{formatFlowAmount(entry.payment_amount, flowType)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          // Total = investor take-home: capital + net_interest_amount.
                          // For GU rows net = gross interest (no deduction).
                          // For NG rows net = gross − wht.
                          const capital = parseFloat(entry.capital_amount) || 0;
                          const net = parseFloat(entry.net_interest_amount);
                          const netSafe = Number.isFinite(net) ? net : (parseFloat(entry.interest_amount) || 0);
                          const total = capital + netSafe;
                          if (total === 0) return '-';
                          return formatFlowAmount(total, flowType);
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(runningBalances[page * effectivePageSize + index])}</strong>
                      </TableCell>
                      {renderSettlementCells(entry, runningBalances[page * effectivePageSize + index])}
                      {canOverride && (
                        <TableCell align="center" sx={{ p: 0 }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            {hasOverride && onShowOverrides && (
                              <Tooltip title="View / revert overrides on this row" arrow>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => onShowOverrides(entry)}
                                  sx={{ p: 0.25 }}
                                >
                                  <FlagIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isOverridable(entry.narration) && (
                              <Tooltip title="Apply override" arrow>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => onOverrideRequest(entry)}
                                  sx={{ p: 0.25 }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                }))
            )}
          </TableBody>
        </Table>
        {!showAllRows && (
          <TablePagination {...pagination.tableProps(totalRowCount)} />
        )}
      </TableContainer>
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="caption" display="block" gutterBottom>
          <strong>Legend:</strong>
        </Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="caption" display="block">
              <Chip label="IN" size="small" color="info" sx={{ mr: 1, height: 18 }} />
              Capital Inflow - Money received from investor
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              <Chip label="OUT" size="small" color="error" sx={{ mr: 1, height: 18 }} />
              Capital/Interest Outflow - Money paid to investor
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              <Chip label="ACC" size="small" color="warning" sx={{ mr: 1, height: 18 }} />
              Accrual - Interest accrued (non-cash entry)
            </Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="caption" display="block">
              • <span style={{ color: '#1976d2' }}>Blue amounts</span> = Inflows
            </Typography>
            <Typography variant="caption" display="block">
              • <span style={{ color: '#d32f2f' }}>(Red amounts in parentheses)</span> = Outflows
            </Typography>
            {hasWhtData && (
              <Typography variant="caption" display="block">
                • <span style={{ color: '#9c27b0' }}>(Purple amounts)</span> = Withholding Tax deducted
              </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              • <Chip label="Adhoc Capital Injection" size="small" color="success" variant="outlined" sx={{ mr: 0.5, height: 18 }} /> Additional capital added
            </Typography>
            <Typography variant="caption" display="block">
              • <Chip label="Adhoc Capital Withdrawal" size="small" color="warning" variant="outlined" sx={{ mr: 0.5, height: 18 }} /> Capital returned early
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              • <Box component="span" sx={{ backgroundColor: NARRATION_ROW_STYLES.override.bg, px: 0.5, borderRadius: 0.5 }}>Yellow rows</Box> = Manual override applied
              {' '}<Chip label="Override" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem' }} />
            </Typography>
            <Typography variant="caption" display="block">
              • <Chip label="ADJ" size="small" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', color: ADJUSTMENT_CHIP_COLOR, borderColor: ADJUSTMENT_CHIP_COLOR, fontWeight: 600 }} /> = Interest Accrual Adjustment (manual interest carry-over, + adds / − reduces)
            </Typography>
            <Typography variant="caption" display="block">
              • <FunctionsIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> Click formula icon on interest amounts to view calculation detail
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {/* Calculation Detail Popover */}
      <CalculationDetailPopover
        anchorEl={auditAnchorEl}
        onClose={handleAuditClose}
        entryId={auditEntryId}
        inlineRecord={auditInlineRecord}
        contractId={contractId}
      />
    </Box>
  );
}

export default AmortizationScheduleTable;
